import { useEffect, useState } from "react";
import DraggableTaskItem from "../tasks/DraggableTaskItem";
import DroppableSection from "../tasks/DroppableSection";
import TaskDropZone from "../tasks/TaskDropZone";
import DraggableSection from "../tasks/DraggableSection";
import SectionDropZone from "../tasks/SectionDropZone";
import { Task, TaskExample, TaskTag, TaskTagTask, proficiencyLevelsOfTask, aimsOfTask, TaskSection, emptyTaskSection } from "../interfaces/Task";
import { useNavigate } from "react-router-dom";
import TaskItemExample from "../tasks/TaskItemExample";
import { Accordion, Tab, Tabs, Button, Modal, Form } from "react-bootstrap";
import TaskItemShared from "../tasks/TaskItemShared";
import { useTranslation } from "react-i18next";
import { getSharedTasksConcrete, getSharedTasksGroups, getTaskExamples, getUserTasks } from "../../services/tasks";
import { LoadingSpinner } from "../elements/LoadingSpinner";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

export default function MyRecentTasks() {
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const [tabKey, setTabKey] = useState('my-tasks');

    const userId = localStorage.getItem('userId') ?? '';

    const [tasks, setTasks] = useState<Task[]>([]);
    const [selectedTag, setSelectedTag] = useState('');
    const [selectedProficiencyLevel, setSelectedProficiencyLevel] = useState('');
    const [selectedAim, setSelectedAim] = useState('');
    const [selectedTagExamples, setSelectedTagExamples] = useState('');
    const [selectedProficiencyLevelExamples, setSelectedProficiencyLevelExamples] = useState('');
    const [selectedAimExamples, setSelectedAimExamples] = useState('');
    const [taskTagTasksExamples, setTasksTagTasksExamples] = useState<TaskTagTask[]>([]);
    const [taskTagTasks, setTasksTagTasks] = useState<TaskTagTask[]>([]);
    const [sections, setSections] = useState<TaskSection[]>([]);
    const [sectionsLoaded, setSectionsLoaded] = useState(false);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState<TaskSection | null>(null);
    const [sectionName, setSectionName] = useState('');
    const [tasksLoaded, setTasksLoaded] = useState(false);
    const [taskId, setTaskId] = useState('');
    const [showExamples, setShowExamples] = useState(false);
    const [examples, setExamples] = useState<TaskExample[]>([]);
    const [examplesLoaded, setExamplesLoaded] = useState(false);
    const [sharedConcrete, setSharedConcrete] = useState<any[]>([]);
    const [sharedConcreteLoaded, setSharedConcreteLoaded] = useState(false);
    const [sharedGroups, setSharedGroups] = useState<any[]>([]);
    const [sharedGroupsLoaded, setSharedGroupsLoaded] = useState(false);
    const [tags, setTags] = useState<TaskTag[]>([]);
    const [tagsLoaded, setTagsLoaded] = useState(false);
    const [taskTags, setTaskTags] = useState<TaskTagTask[]>([]);
    const [taskTagsLoaded, setTaskTagsLoaded] = useState(false);

    const handleNewTask = () => {
        // Create a new task
        const requestOptionsPost = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify({
                name: 'New task',
                pretasks: [],
                tasks: [],
                postasks: [],
                updated: new Date(),
                userId: userId,
                active: false
            })
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task', requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                const taskId =  data._id;
                setTaskId(taskId);
                
                // Decide if user has to set level or not
                const requestOptions = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + localStorage.getItem('token')
                    }
                };
                fetch(import.meta.env.VITE_API_URL + '/v1/user/' + userId, requestOptions)
                    .then(response => {
                        if (response.status !== 200) {
                            navigate('/');
                        }
                        return response.json();
                    })
                    .then(data => {
                        navigate('/edit-task/' + taskId);
                    });
            });
    }

    const handleDeleteTask = (id: string) => {
        const newTasks = tasks.filter((t: Task) => t._id != id);
        setTasks(newTasks);
    }

    const handleDuplicateTask = (task: Task) => {
        const newTask = Object.assign({}, task);
        newTask.name = task.name;
        const newTasks = [newTask, ...tasks];
        setTasks(newTasks);
    }

    const handleDuplicateTaskExample = (task: Task) => {
        const newTask = Object.assign({}, task);
        const newTasks = [newTask, ...tasks];
        setTasks(newTasks);
        setShowExamples(false);
        alert(t("The task has been cloned. You will find the cloned task in \"My Task designs\" in the dashboard"))
    }

    const handleDuplicateTaskShared = (task: Task) => {
        const newTask = Object.assign({}, task);
        const newTasks = [newTask, ...tasks];
        setTasks(newTasks);
        setShowExamples(false);
        alert(t("The task has been cloned. You will find the cloned task in \"My Task designs\" in the dashboard"))
    }

    const handleShowExamples = () => {
        setTabKey('example-tasks');
    }

    useEffect(() => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };

        // Load tags
        fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/' + userId, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(data => {
                setTags(data);
                setTagsLoaded(true);
            });

        // Load sections
        fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + userId, requestOptions)
            .then(response => {
                return response.json();
            })
            .then(data => {
                setSections(data || []);
                setSectionsLoaded(true);
            })
            .catch(() => {
                setSections([]);
                setSectionsLoaded(true);
            });

        getUserTasks(userId)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setTasks(data);
                setTasksLoaded(true);
            });

        // Load example tasks
        getTaskExamples()
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                console.log('data',  data);
                setExamples(data);
                setExamplesLoaded(true);
            });   
        
        // Load shared tasks
        getSharedTasksConcrete(userId)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                const newSharedConcrete: any[] = [];
                if (data.length > 0) {
                    data.sort((a, b) => a.updated > b.updated ? 1 : -1).map((d: any) => {
                        if (d.task != null) {
                            if (newSharedConcrete.findIndex((i: any) => i.id == d.task._id) === -1) {
                                // Safely get user full name
                                let userFullName = 'Unknown';
                                if (d.user && d.user.profile) {
                                    userFullName = [d.user.profile.firstname, d.user.profile.lastname].filter(Boolean).join(' ') || 'Unknown';
                                }
                                newSharedConcrete.push({
                                    _id: d.task._id,
                                    name: d.task.name,
                                    updated: d.task.updated,
                                    userId: d.task.userId,
                                    userFullName: userFullName
                                });
                            }
                        }
                    });
                }
                setSharedConcrete(newSharedConcrete);
                setSharedConcreteLoaded(true);
            });

        getSharedTasksGroups(userId)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                const newSharedGroups: any[] = [];
                if (data.length > 0) {
                    data.sort((a, b) => {
                        // Handle null tasks in sorting
                        if (!a.task || !b.task) return 0;
                        return a.task.updated > b.task.updated ? 1 : -1;
                    }).map((d: any) => {
                        if (d.task != null) {
                            if (newSharedGroups.findIndex((i: any) => i.id == d.task._id) === -1) {
                                // Safely get group name
                                const groupName = d.group && d.group.name ? d.group.name : 'Unknown';
                                newSharedGroups.push({
                                    _id: d.task._id,
                                    name: d.task.name,
                                    updated: d.task.updated,
                                    group: groupName
                                });
                            }
                        }
                    });
                }
                setSharedGroups(newSharedGroups);
                setSharedGroupsLoaded(true);
            });
    }, []);


    const handleFilterTag = (event: any) => {
        const tagId = event.currentTarget.value;
        setSelectedTag(tagId);
        if (tagId != "") {
            const requestOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/tag/' + tagId, requestOptions)
                .then(response => {
                    return response.json();
                })
                .then(data => {
                    setTasksTagTasks(data);
                });
        } else {
            setTasksTagTasks([]);
        }
    }

    const handleFilterProficiencyLevel = (event: any) => {
        const levelValue = event.currentTarget.value;
        setSelectedProficiencyLevel(levelValue);
    }

    const handleFilterAim = (event: any) => {
        const aimValue = event.currentTarget.value;
        setSelectedAim(aimValue);
    }

    const handleFilterTagExamples = (event: any) => {
        const tagId = event.currentTarget.value;
        setSelectedTagExamples(tagId);
        if (tagId != "") {
            const requestOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-tags/tag/' + tagId, requestOptions)
                .then(response => {
                    return response.json();
                })
                .then(data => {
                    setTasksTagTasksExamples(data);
                });
        } else {
            setTasksTagTasksExamples([]);
        }
    }

    const handleFilterProficiencyLevelExamples = (event: any) => {
        const levelValue = event.currentTarget.value;
        setSelectedProficiencyLevelExamples(levelValue);
    }

    const handleFilterAimExamples = (event: any) => {
        const aimValue = event.currentTarget.value;
        setSelectedAimExamples(aimValue);
    }

    const handleCreateSection = () => {
        setEditingSection(null);
        setSectionName('');
        setShowSectionModal(true);
    }

    const handleEditSection = (section: TaskSection) => {
        setEditingSection(section);
        setSectionName(section.name);
        setShowSectionModal(true);
    }

    const handleSaveSection = () => {
        if (!sectionName.trim()) return;

        if (editingSection) {
            // Update existing section
            const requestOptions = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify({
                    ...editingSection,
                    name: sectionName.trim()
                })
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + editingSection._id, requestOptions)
                .then(response => response.json())
                .then(data => {
                    setSections(sections.map(s => s._id === editingSection._id ? data : s));
                    setShowSectionModal(false);
                });
        } else {
            // Create new section
            const newSection = {
                name: sectionName.trim(),
                userId: userId,
                weight: sections.length,
                expanded: true
            };
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(newSection)
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections', requestOptions)
                .then(response => response.json())
                .then(data => {
                    setSections([...sections, data]);
                    setShowSectionModal(false);
                });
        }
    }

    const handleDeleteSection = (sectionId: string) => {
        if (window.confirm(t("Are you sure you want to delete this section? Tasks in this section will be moved to 'Unsectioned'."))) {
            const requestOptions = {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + sectionId, requestOptions)
                .then(() => {
                    setSections(sections.filter(s => s._id !== sectionId));
                    // Move tasks from deleted section to unsectioned
                    setTasks(tasks.map(t => t.sectionId === sectionId ? {...t, sectionId: undefined} : t));
                });
        }
    }

    const handleToggleSection = (sectionId: string) => {
        const section = sections.find(s => s._id === sectionId);
        if (section) {
            const updatedSection = { ...section, expanded: !section.expanded };
            const requestOptions = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(updatedSection)
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + sectionId, requestOptions)
                .then(response => response.json())
                .then(data => {
                    setSections(sections.map(s => s._id === sectionId ? data : s));
                });
        }
    }

    const handleTaskDrop = (taskId: string, targetSectionId?: string, insertIndex?: number) => {
        const updatedTask = tasks.find(t => t._id === taskId);
        if (!updatedTask) return;

        // If moving to same section and same position, do nothing
        if (updatedTask.sectionId === targetSectionId && insertIndex === undefined) {
            return;
        }

        let newTasks = [...tasks];

        // Get tasks in target section
        const targetSectionTasks = newTasks.filter(t => t.sectionId === targetSectionId);

        // Calculate new weight
        let newWeight = updatedTask.weight;

        if (insertIndex !== undefined) {
            // Position-based drop
            if (insertIndex === 0) {
                // Drop at beginning
                const minWeight = targetSectionTasks.length > 0 ? Math.min(...targetSectionTasks.map(t => t.weight)) : 0;
                newWeight = minWeight - 1;
            } else if (insertIndex >= targetSectionTasks.length) {
                // Drop at end
                const maxWeight = targetSectionTasks.length > 0 ? Math.max(...targetSectionTasks.map(t => t.weight)) : 0;
                newWeight = maxWeight + 1;
            } else {
                // Drop between tasks
                const sortedTasks = targetSectionTasks.sort((a, b) => a.weight - b.weight);
                const beforeTask = sortedTasks[insertIndex - 1];
                const afterTask = sortedTasks[insertIndex];
                newWeight = (beforeTask.weight + afterTask.weight) / 2;
            }
        } else {
            // Section-only drop (append to end)
            const maxWeight = targetSectionTasks.length > 0 ? Math.max(...targetSectionTasks.map(t => t.weight)) : 0;
            newWeight = maxWeight + 1;
        }

        const taskUpdate = {
            ...updatedTask,
            sectionId: targetSectionId,
            weight: newWeight
        };

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(taskUpdate)
        };

        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + taskId, requestOptions)
            .then(response => response.json())
            .then(data => {
                // Update local state
                setTasks(tasks.map(t => t._id === taskId ? data : t));
            })
            .catch(error => {
                console.error('Error moving task:', error);
            });
    }

    const handleSectionDrop = (sectionId: string, insertIndex: number) => {
        const draggedSection = sections.find(s => s._id === sectionId);
        if (!draggedSection) return;

        // Create a new array without the dragged section
        let reorderedSections = sections.filter(s => s._id !== sectionId);

        // Insert the dragged section at the new position
        reorderedSections.splice(insertIndex, 0, draggedSection);

        // Update weights for all sections
        const updatedSections = reorderedSections.map((section, index) => ({
            ...section,
            weight: index
        }));

        // Update all sections in the backend
        const updatePromises = updatedSections.map(section => {
            const requestOptions = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(section)
            };
            return fetch(import.meta.env.VITE_API_URL + '/v1/task-sections/' + section._id, requestOptions);
        });

        Promise.all(updatePromises)
            .then(() => {
                setSections(updatedSections);
            })
            .catch(error => {
                console.error('Error reordering sections:', error);
            });
    }

    const handleUnshareTask = (taskId: string) => {
        const newSharedConcrete = sharedConcrete.filter((t: Task) => t._id == taskId);
        setSharedConcrete(newSharedConcrete);
    }

    return(        
        <div className="my-recent-tasks box">
            <Tabs id="task-tabs" defaultActiveKey={"my-tasks"} activeKey={ tabKey } onSelect={(k) => setTabKey(k ?? 'my-tasks')}>
                <Tab eventKey="my-tasks" title={ t("My task designs") }>
                    { !tasksLoaded && <LoadingSpinner /> }
                    { tasksLoaded && tasks.length == 0 && !showExamples &&
                        <div className="no-tasks">
                            <div className="margin-bottom-small">
                                <button className="btn btn-primary btn-lg" onClick={ handleNewTask }>
                                    { t("Wish to design a task?") }
                                </button>
                            </div>
                            <div className="margin-bottom-small">
                                { t("or get some inspiration") }
                            </div>
                            <button className="btn btn-primary" onClick={ handleShowExamples }>
                                { t("See some examples") }
                            </button>
                        </div>
                    }
                    { tasksLoaded && tasks.length > 0 && !showExamples &&
                        <div className="has-tasks">
                            <div className="actions">
                                { tagsLoaded &&
                                    <div className="actio-filters">
                                        <select name="selected_tag" className="tag-filter" onChange={ handleFilterTag }>
                                            <option value="">{ t("- Select a tag -") }</option>
                                            {
                                                tags.map((t: TaskTag) => {
                                                    return <option key={ 'tag-' + t._id} value={ t._id}>
                                                        { t.name }
                                                    </option>
                                                })
                                            }
                                        </select>
                                        <select name="selected_proficiency_level" className="proficiency-filter" onChange={ handleFilterProficiencyLevel }>
                                            <option value="">{ t("- Select a proficiency level -") }</option>
                                            {
                                                proficiencyLevelsOfTask.map((level) => {
                                                    return <option key={ 'level-' + level.value} value={ level.value}>
                                                        { level.description }
                                                    </option>
                                                })
                                            }
                                        </select>
                                        <select name="selected_aim" className="aim-filter" onChange={ handleFilterAim }>
                                            <option value="">{ t("- Select an aim -") }</option>
                                            {
                                                aimsOfTask.map((aim) => {
                                                    return <option key={ 'aim-' + aim.value} value={ aim.value}>
                                                        { aim.description }
                                                    </option>
                                                })
                                            }
                                        </select>
                                    </div>
                                }
                                <div className="action-buttons">
                                    <a className="action-link create-section" title={ t("Create a new section") }
                                        onClick={ handleCreateSection }>
                                        { t("Create Section") }
                                    </a>
                                    <a className="action-link create-task" title={ t("Create a new task") }
                                        onClick={ handleNewTask }>
                                        { t("Create a new task") }
                                    </a>
                                </div>
                            </div>
                            <DndProvider backend={HTML5Backend}>
                                <div className="tasks-sections">
                                    { sectionsLoaded &&
                                        <Accordion defaultActiveKey={[...sections.filter(s => s.expanded).map(s => s._id), 'unsectioned']} alwaysOpen>
                                            {/* Drop zone at the beginning for sections */}
                                            <SectionDropZone
                                                insertIndex={0}
                                                onDrop={handleSectionDrop}
                                            />
                                            {/* Render sections */}
                                            {sections.sort((a, b) => a.weight - b.weight).map((section, index) => {
                                                const sectionTasks = tasks.filter(t => t.sectionId === section._id);
                                                const filteredTasks = sectionTasks.filter(t => {
                                                    const tagIndex = taskTagTasks.findIndex((tt: TaskTagTask) => tt.taskId == t._id);
                                                    const tagMatches = selectedTag == '' || (selectedTag != '' && taskTagTasks.length > 0 && tagIndex !== -1);
                                                    const proficiencyMatches = selectedProficiencyLevel == '' || (selectedProficiencyLevel != '' && t.proficiency_level && t.proficiency_level.includes(parseInt(selectedProficiencyLevel)));
                                                    const aimMatches = selectedAim == '' || (selectedAim != '' && t.aims && t.aims.includes(parseInt(selectedAim)));
                                                    return tagMatches && proficiencyMatches && aimMatches;
                                                });

                                                return (
                                                    <div key={'section-container-' + section._id}>
                                                        <DraggableSection section={section}>
                                                            {(dragHandleRef) => (
                                                                <Accordion.Item eventKey={section._id}>
                                                                    <Accordion.Header>
                                                                        <div className="section-header">
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                <div
                                                                                    ref={dragHandleRef}
                                                                                    className="section-drag-handle"
                                                                                    style={{
                                                                                        cursor: 'grab',
                                                                                        fontSize: '18px',
                                                                                        userSelect: 'none',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        padding: '0 4px'
                                                                                    }}
                                                                                    title={t("Drag to reorder")}
                                                                                >
                                                                                    <i className="bi bi-grip-vertical"></i>
                                                                                </div>
                                                                                <span>{section.name} ({filteredTasks.length})</span>
                                                                            </div>
                                                                            <div className="section-actions">
                                                                                <div
                                                                                    className="section-edit-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleEditSection(section);
                                                                                    }}
                                                                                    role="button"
                                                                                    tabIndex={0}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                                            e.stopPropagation();
                                                                                            handleEditSection(section);
                                                                                        }
                                                                                    }}
                                                                                    title={t("Edit section")}
                                                                                >
                                                                                </div>
                                                                                <div
                                                                                    className="section-delete-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDeleteSection(section._id);
                                                                                    }}
                                                                                    role="button"
                                                                                    tabIndex={0}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                                            e.stopPropagation();
                                                                                            handleDeleteSection(section._id);
                                                                                        }
                                                                                    }}
                                                                                    title={t("Delete section")}
                                                                                >
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Accordion.Header>
                                                                <Accordion.Body>
                                                                    <DroppableSection
                                                                        sectionId={section._id}
                                                                        onDrop={handleTaskDrop}
                                                                        className="section-tasks"
                                                                    >
                                                                        {/* Drop zone at the beginning */}
                                                                        <TaskDropZone
                                                                            sectionId={section._id}
                                                                            insertIndex={0}
                                                                            onDrop={handleTaskDrop}
                                                                        />

                                                                        {filteredTasks.sort((a, b) => a.weight - b.weight).map((t: Task, index) => (
                                                                            <div key={'task-container-' + t._id}>
                                                                                <DraggableTaskItem
                                                                                    position={index}
                                                                                    deleteCallback={handleDeleteTask}
                                                                                    duplicateCallback={handleDuplicateTask}
                                                                                    task={t}
                                                                                    sectionId={section._id}
                                                                                    key={'task-' + t._id}
                                                                                />
                                                                                {/* Drop zone after each task */}
                                                                                <TaskDropZone
                                                                                    sectionId={section._id}
                                                                                    insertIndex={index + 1}
                                                                                    onDrop={handleTaskDrop}
                                                                                />
                                                                            </div>
                                                                        ))}

                                                                        {filteredTasks.length === 0 && (
                                                                            <p className="text-muted">{t("No tasks in this section")}</p>
                                                                        )}
                                                                    </DroppableSection>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                            )}
                                                        </DraggableSection>
                                                        {/* Drop zone after each section */}
                                                        <SectionDropZone
                                                            insertIndex={index + 1}
                                                            onDrop={handleSectionDrop}
                                                        />
                                                    </div>
                                                );
                                            })}

                                            {/* Render unsectioned tasks */}
                                            {(() => {
                                                const unsectionedTasks = tasks.filter(t => !t.sectionId);
                                                const filteredUnsectionedTasks = unsectionedTasks.filter(t => {
                                                    const tagIndex = taskTagTasks.findIndex((tt: TaskTagTask) => tt.taskId == t._id);
                                                    const tagMatches = selectedTag == '' || (selectedTag != '' && taskTagTasks.length > 0 && tagIndex !== -1);
                                                    const proficiencyMatches = selectedProficiencyLevel == '' || (selectedProficiencyLevel != '' && t.proficiency_level && t.proficiency_level.includes(parseInt(selectedProficiencyLevel)));
                                                    const aimMatches = selectedAim == '' || (selectedAim != '' && t.aims && t.aims.includes(parseInt(selectedAim)));
                                                    return tagMatches && proficiencyMatches && aimMatches;
                                                });

                                                if (filteredUnsectionedTasks.length > 0) {
                                                    return (
                                                        <Accordion.Item eventKey="unsectioned">
                                                            <Accordion.Header>
                                                                {t("Unsectioned")} ({filteredUnsectionedTasks.length})
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                <DroppableSection
                                                                    sectionId={undefined}
                                                                    onDrop={handleTaskDrop}
                                                                    className="section-tasks"
                                                                >
                                                                    {/* Drop zone at the beginning */}
                                                                    <TaskDropZone
                                                                        sectionId={undefined}
                                                                        insertIndex={0}
                                                                        onDrop={handleTaskDrop}
                                                                    />

                                                                    {filteredUnsectionedTasks.sort((a, b) => a.weight - b.weight).map((t: Task, index) => (
                                                                        <div key={'task-container-' + t._id}>
                                                                            <DraggableTaskItem
                                                                                position={index}
                                                                                deleteCallback={handleDeleteTask}
                                                                                duplicateCallback={handleDuplicateTask}
                                                                                task={t}
                                                                                sectionId={undefined}
                                                                                key={'task-' + t._id}
                                                                            />
                                                                            {/* Drop zone after each task */}
                                                                            <TaskDropZone
                                                                                sectionId={undefined}
                                                                                insertIndex={index + 1}
                                                                                onDrop={handleTaskDrop}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </DroppableSection>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </Accordion>
                                    }
                                </div>
                            </DndProvider>
                        </div>
                    }
                </Tab>
                <Tab eventKey="shared-tasks" title={ t("Shared with me") }>
                    <div className="shared-container">
                        <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>{ t("Shared directly with me") }</Accordion.Header>
                                <Accordion.Body>
                                    { !sharedConcreteLoaded && <LoadingSpinner /> }
                                    { sharedConcreteLoaded && 
                                        sharedConcrete.map((e: Task) => {
                                            return <TaskItemShared task={ e } key={ 'task-shared-' + e._id }
                                                duplicateCallback={ handleDuplicateTaskExample }
                                                deleteCallback={ handleUnshareTask }
                                                 />;
                                        })
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>{ t("Shared by group") }</Accordion.Header>
                                <Accordion.Body>
                                    { !sharedGroupsLoaded && <LoadingSpinner /> }
                                    { sharedGroupsLoaded && 
                                        sharedGroups.map((e: Task) => {
                                            return <TaskItemShared task={ e } duplicateCallback={ handleDuplicateTaskExample } deleteCallback={ handleUnshareTask }
                                                key={ 'task-shared-' + e._id } />;
                                        })
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </div>
                </Tab>
                <Tab eventKey="example-tasks" title={ t("Task examples") }>
                    <div className="examples-container">
                        { examplesLoaded && examples.length > 0 &&
                            <div className="has-tasks">
                                <div className="actions">
                                    { tagsLoaded &&
                                        <div className="actio-filters">
                                            <select name="selected_proficiency_level_examples" className="proficiency-filter" onChange={ handleFilterProficiencyLevelExamples }>
                                                <option value="">{ t("- Select a proficiency level -") }</option>
                                                {
                                                    proficiencyLevelsOfTask.map((level) => {
                                                        return <option key={ 'level-examples-' + level.value} value={ level.value}>
                                                            { level.description }
                                                        </option>
                                                    })
                                                }
                                            </select>
                                            <select name="selected_aim_examples" className="aim-filter" onChange={ handleFilterAimExamples }>
                                                <option value="">{ t("- Select an aim -") }</option>
                                                {
                                                    aimsOfTask.map((aim) => {
                                                        return <option key={ 'aim-examples-' + aim.value} value={ aim.value}>
                                                            { aim.description }
                                                        </option>
                                                    })
                                                }
                                            </select>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                        { !examplesLoaded && <LoadingSpinner /> }
                        { examplesLoaded &&
                            examples.filter((e: TaskExample) => {
                                // Filter out examples with null tasks
                                if (!e.task || !e.task._id) {
                                    return false;
                                }
                                const tagIndex = taskTagTasksExamples.findIndex((tt: TaskTagTask) => tt.taskId == e.task._id);
                                const tagMatches = selectedTagExamples == '' || (selectedTagExamples != '' && taskTagTasksExamples.length > 0 && tagIndex !== -1);
                                const proficiencyMatches = selectedProficiencyLevelExamples == '' || (selectedProficiencyLevelExamples != '' && (e.task as any).proficiency_level && (e.task as any).proficiency_level.includes(parseInt(selectedProficiencyLevelExamples)));
                                const aimMatches = selectedAimExamples == '' || (selectedAimExamples != '' && (e.task as any).aims && (e.task as any).aims.includes(parseInt(selectedAimExamples)));
                                return tagMatches && proficiencyMatches && aimMatches;
                            }).map((e: TaskExample) => {
                                return <TaskItemExample task={ e } duplicateCallback={ handleDuplicateTaskShared }
                                    key={ 'task-example-' + e._id } />;
                            })
                        }
                    </div>
                </Tab>
            </Tabs>

            {/* Section Modal */}
            <Modal show={showSectionModal} onHide={() => setShowSectionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingSection ? t("Edit Section") : t("Create Section")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>{t("Section Name")}</Form.Label>
                            <Form.Control
                                type="text"
                                value={sectionName}
                                onChange={(e) => setSectionName(e.target.value)}
                                placeholder={t("Enter section name")}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSectionModal(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSaveSection} disabled={!sectionName.trim()}>
                        {editingSection ? t("Update") : t("Create")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>);
}
